import { useState, type FormEvent } from "react";
import { categories } from "@/mocks/categories";
import { Link } from "react-router-dom";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get("company_alt") as string || "").trim();
    if (honeypotValue) {
      setFormStatus("success");
      setFormMessage("Thank you for subscribing!");
      setEmail("");
      return;
    }

    formData.delete("company_alt");

    try {
      const response = await fetch("https://readdy.ai/api/form/d993cuj558j8vijmgvt0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setFormMessage("Thank you for subscribing! Check your inbox for updates.");
        setEmail("");
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || "Something went wrong. Please try again.";
        if (serverMsg.toLowerCase().includes("spam")) {
          setFormStatus("error");
          setFormMessage("Something went wrong. Please try again.");
        } else {
          setFormStatus("error");
          setFormMessage(serverMsg);
        }
      }
    } catch {
      setFormStatus("error");
      setFormMessage("Network error. Please check your connection and try again.");
    }
  };

  return (
    <footer className="w-full bg-foreground-900 text-background-200">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-12">
          <div>
            <span className="font-heading text-xl font-bold text-background-50">hipobuy sheet</span>
            <p className="mt-2 text-sm text-foreground-400">hipobuyspread.cyou</p>
          </div>
          <p className="text-sm text-foreground-400 max-w-sm">
            Your trusted source for organized product spreadsheets, verified finds, and expert shopping guides since 2025.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
          <div>
            <h4 className="text-background-50 font-semibold text-sm mb-4">Categories</h4>
            <div className="flex flex-col gap-2">
              {categories.slice(0, 6).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-background-50 font-semibold text-sm mb-4">More Categories</h4>
            <div className="flex flex-col gap-2">
              {categories.slice(6).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-background-50 font-semibold text-sm mb-4">Resources</h4>
            <div className="flex flex-col gap-2">
              <Link to="/hipobuy-spreadsheet-guide" className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">Spreadsheet Guide</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">Main Website</a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">Product Database</a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">Latest Updates</a>
            </div>
          </div>
          <div>
            <h4 className="text-background-50 font-semibold text-sm mb-4">Stay Updated</h4>
            <p className="text-sm text-foreground-400 mb-3 leading-relaxed">
              Weekly hipobuy spreadsheet updates delivered to your inbox.
            </p>
            <form
              onSubmit={handleSubscribe}
              data-readdy-form="true"
              className="flex flex-col gap-2"
            >
              <div className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 bg-foreground-800 border border-foreground-700 rounded-lg px-4 py-2.5 text-sm text-background-50 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500 transition-colors"
                />
                <button
                  type="submit"
                  className="bg-accent-500 text-foreground-950 px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-all whitespace-nowrap cursor-pointer"
                >
                  Subscribe
                </button>
              </div>
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="antispam-field"
              />
              {formStatus === "success" && (
                <p className="text-xs text-accent-400 mt-1">{formMessage}</p>
              )}
              {formStatus === "error" && (
                <p className="text-xs text-red-400 mt-1">{formMessage}</p>
              )}
            </form>
          </div>
        </div>

        <div className="border-t border-foreground-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-6">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">Twitter</a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">Pinterest</a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">Reddit</a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">Discord</a>
          </div>
          <div className="text-right">
            <p className="text-background-50 font-heading font-bold text-xl">&copy; hipobuyspread.cyou 2025</p>
            <p className="text-xs text-foreground-500 mt-1 font-serif">All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}